import { wsConfig } from '@ui/lib/wallet/config';
import { ToastOps, TxnMutateCallBackParams } from '@ui/types/tx';
import { extractErrorMessage } from '@ui/utils/extractContractError';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useWalletClient, useWriteContract } from 'wagmi';
import { waitForTransactionReceipt } from 'wagmi/actions';

import { useDataChainId } from './datachain';
import { useMutationToast } from './useMutationToast';

export const useTxnWriteContract = (p?: TxnMutateCallBackParams & ToastOps) => {
  const { t } = useTranslation();

  const {
    onSuccess,
    onError,
    onSettled,
    onMutate,
    onReceiptSettled,
    onMutateToastOpts,
    onSuccessToastOpts,
    onErrorToastOpts,
    onReceiptSettledToastOpts,
  } = p ?? {};
  const chainId = useDataChainId();

  const mutationToastInfo = useMutationToast();
  const { data: client } = useWalletClient();
  const handleErr = (e: Error) => {
    const errorMsg = extractErrorMessage(e.toString());

    if (!isNil(errorMsg)) {
      mutationToastInfo.onError({
        errorMsg: errorMsg,
        ...onErrorToastOpts,
      });
    } else {
      mutationToastInfo.onError({ onErrorToastOpts });
    }

    onError?.(e);
  };

  const handleSettled = () => {
    mutationToastInfo.onSettled();

    onSettled?.();
  };

  return useWriteContract({
    mutation: {
      onMutate() {
        mutationToastInfo.onMutate(onMutateToastOpts);
        onMutate?.();
      },
      onError: (e) => {
        handleErr(e);
        handleSettled();
      },
      async onSuccess(data) {
        if (!client) return;
        try {
          if (onReceiptSettled) {
            mutationToastInfo.onSettled();
            mutationToastInfo.onSuccess({
              ...(onReceiptSettledToastOpts
                ? onReceiptSettledToastOpts
                : {
                    title: t('Transaction Receipt Submitted'), // generic txn receipt sent msg
                    description: t(
                      'The transaction receipt was submitted successfully.',
                    ),
                    action: undefined,
                  }),
            });
            onReceiptSettled();
          }
          const receipt = await waitForTransactionReceipt(wsConfig, {
            hash: data,
            chainId,
          });

          mutationToastInfo.onSuccess({
            ...(typeof onSuccessToastOpts === 'function'
              ? onSuccessToastOpts(receipt.transactionHash)
              : onSuccessToastOpts),
            txHash: receipt.transactionHash,
          });
          onSuccess?.(receipt.transactionHash);
        } catch (e) {
          handleErr(e as Error);
        } finally {
          handleSettled();
        }
      },
    },
  });
};
